import React from 'react';
import clsx from 'clsx';

type BadgeType = 'instance' | 'server' | 'order' | 'job' | 'release' | 'product' | 'plan' | 'template' | 'customer';

interface StatusBadgeProps {
  status: string;
  type?: BadgeType;
}

function getColor(status: string): string {
  const s = status.toLowerCase();

  // Green — healthy / success states
  if (
    ['active', 'completed', 'ready', 'deployed', 'approved', 'paid'].includes(s)
  ) {
    return 'bg-green-100 text-green-800';
  }

  // Red — error / terminal bad states
  if (
    ['failed', 'decommissioned', 'terminated', 'deleted', 'rolled_back'].includes(s)
  ) {
    return 'bg-red-100 text-red-800';
  }

  // Blue — in-progress states (kept distinct from brand green so it doesn't
  // visually collide with the "healthy/success" green badges above)
  if (
    [
      'deploying',
      'running',
      'provisioning',
      'preparing',
      'rolling_out',
      'partially_deployed',
      'upgrading',
      'downgrading',
      'retrying',
    ].includes(s)
  ) {
    return 'bg-blue-100 text-blue-800';
  }

  // Grau — abgelegt, nicht kaputt.
  //
  // Ausdruecklich nicht rot: `archived` ist kein Fehler und kein Ende, sondern
  // eine bewusste Ablage. Rot neben `failed` zu stehen wuerde es zu einem
  // Problem machen, das es nicht ist.
  if (['archived'].includes(s)) {
    return 'bg-slate-100 text-slate-600';
  }

  // Yellow — warning / paused states
  if (
    [
      'suspended',
      'cancelled',
      'cancellation_scheduled',
      'draining',
      'maintenance',
      'full',
      'scheduled',
      'deprecated',
    ].includes(s)
  ) {
    return 'bg-yellow-100 text-yellow-800';
  }

  // Purple — special states
  if (['testing'].includes(s)) {
    return 'bg-purple-100 text-purple-800';
  }

  // Gray — neutral / initial states
  // pending, draft, archived, inactive, deleting, pending_payment, pending_provisioning
  return 'bg-slate-100 text-slate-700';
}

function formatLabel(status: string): string {
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export default function StatusBadge({ status }: StatusBadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap',
        getColor(status)
      )}
    >
      {formatLabel(status)}
    </span>
  );
}
